import { useEffect, useMemo, useState } from 'react'
import {
  FaBell,
  FaBox,
  FaArrowLeft,
  FaCartShopping,
  FaCircleCheck,
  FaClock,
  FaCreditCard,
  FaFilter,
  FaGavel,
  FaHeart,
  FaListUl,
  FaMagnifyingGlass,
  FaMedal,
  FaRotateRight,
  FaShieldHalved,
  FaSliders,
  FaStore,
  FaTruckFast,
  FaTriangleExclamation,
} from 'react-icons/fa6'
import CountdownTimer from '../components/ui/CountdownTimer'
import apiClient, { resolveAssetUrl } from '../services/apiClient'
import { getStoredUser, getToken, saveSession } from '../services/authStorage'
import { confirmStripePaymentRecovery, hasStripePaymentRecovery } from '../services/stripeRecovery'
import { navigateTo } from '../app/routes'
import {
  auctionActionLabel,
  auctionCanBid,
  auctionClock,
  auctionEndsSoon,
  auctionReserveMet,
  filterAuctionsByFocus,
  formatMoney,
  listingInventoryLabel,
  resolveBidReadiness,
  resolveAuctionStatus,
  resolveShopActionReadiness,
  shopActionIsAvailable,
  sortShopListings,
  titleCase,
} from '../features/marketplace/marketplaceUtils'
import './MarketplacePage.css'

const auctionTypes = [
  { value: 'all', label: 'All formats' },
  { value: 'anti-snipe', label: 'Anti-snipe' },
  { value: 'hard-close', label: 'Hard close' },
  { value: 'live-style', label: 'Live style' },
]

const auctionStatuses = [
  { value: 'all', label: 'All lots' },
  { value: 'live', label: 'Live now' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'won', label: 'Won' },
  { value: 'closed', label: 'Closed' },
]

const auctionFocusOptions = [
  { value: 'all', label: 'Full floor' },
  { value: 'featured', label: 'Featured' },
  { value: 'ending-soon', label: 'Ending soon' },
  { value: 'reserve-open', label: 'Reserve open' },
]

const shopSortOptions = [
  { value: 'newest', label: 'Newest first' },
  { value: 'price-low', label: 'Price low to high' },
  { value: 'price-high', label: 'Price high to low' },
  { value: 'low-stock', label: 'Lowest stock' },
]

const shopAvailabilityOptions = [
  { value: 'all', label: 'All inventory' },
  { value: 'available', label: 'Available only' },
  { value: 'bundle', label: 'Bundle eligible' },
  { value: 'quick', label: 'Quick checkout' },
]

function itemImage(item) {
  return item?.images?.[0]?.url ? resolveAssetUrl(item.images[0].url) : ''
}

function detailId(route, prefix) {
  const match = route.match(new RegExp(`^/${prefix}/([^/]+)$`))
  return match?.[1] || null
}

export default function MarketplacePage({ route }) {
  const shopDetailId = detailId(route, 'shop')
  const auctionDetailId = detailId(route, 'auctions')
  const mode = route.startsWith('/shop') ? 'shop' : 'auctions'

  if (auctionDetailId) {
    return <AuctionDetail auctionId={auctionDetailId} />
  }

  if (shopDetailId) {
    return <ShopDetail listingId={shopDetailId} />
  }

  return mode === 'shop' ? <ShopLane /> : <AuctionLane />
}

function AuctionLane() {
  const [auctions, setAuctions] = useState([])
  const [query, setQuery] = useState('')
  const [status, setStatus] = useState('all')
  const [type, setType] = useState('all')
  const [category, setCategory] = useState('All')
  const [focus, setFocus] = useState('all')
  const [loadState, setLoadState] = useState('Loading auction floor...')
  const [actionStatus, setActionStatus] = useState('')
  const [liveStatus, setLiveStatus] = useState('Connecting live auction floor...')
  const [now, setNow] = useState(Date.now())
  const isLoading = loadState.startsWith('Loading')

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    let mounted = true

    setLoadState('Loading auction floor...')
    apiClient.auctions({ q: query, status, type, category })
      .then((payload) => {
        if (!mounted) return
        setAuctions(payload.auctions || [])
        setLoadState('')
      })
      .catch((error) => {
        if (!mounted) return
        setAuctions([])
        setLoadState(error.message || 'Auction feed is unavailable.')
      })

    return () => {
      mounted = false
    }
  }, [category, query, status, type])

  useEffect(() => {
    if (typeof EventSource === 'undefined') {
      setLiveStatus('Live auction floor unavailable in this browser.')
      return undefined
    }

    const events = new EventSource(apiClient.auctionFloorEventsUrl())

    events.addEventListener('connected', () => {
      setLiveStatus('Live auction floor connected.')
    })

    const applyAuctionEvent = (event, statusMessage) => {
      const payload = JSON.parse(event.data)
      if (!payload.auction?.id) return

      setAuctions((current) => current.map((auction) => (
        auction.id === payload.auction.id ? payload.auction : auction
      )))
      setLiveStatus(statusMessage(payload))
    }

    events.addEventListener('bid-accepted', (event) => {
      applyAuctionEvent(event, (payload) => (
        payload.bid?.extendedClose
          ? `${payload.auction.title} updated. Anti-snipe timer extended.`
          : `${payload.auction.title} updated with a new bid.`
      ))
    })

    events.addEventListener('auction-status-changed', (event) => {
      applyAuctionEvent(event, (payload) => `${payload.auction.title} is now ${titleCase(payload.auction.status)}.`)
    })

    events.onerror = () => {
      setLiveStatus('Reconnecting live auction floor...')
    }

    return () => {
      events.close()
    }
  }, [])

  const refreshAuctions = async (message) => {
    const payload = await apiClient.auctions({ q: query, status, type, category })
    setAuctions(payload.auctions || [])
    setLoadState('')
    if (message) setActionStatus(message)
  }

  const requireSession = () => {
    const token = getToken()
    if (!token) {
      navigateTo('/login')
      return null
    }

    return token
  }

  const handleWatchAuction = async (auction) => {
    const token = requireSession()
    if (!token) return

    try {
      setActionStatus(`Adding ${auction.title} to your watchlist...`)
      await apiClient.addToWatchlist(token, auction.id)
      await refreshAuctions(`${auction.title} is now in your watchlist.`)
    } catch (error) {
      setActionStatus(error.message || 'Could not update watchlist.')
    }
  }

  const handleBidAuction = async (auction) => {
    const token = requireSession()
    if (!token) return

    if (!auctionCanBid(auction)) {
      setActionStatus('This lot is not open for bidding.')
      return
    }

    try {
      setActionStatus(`Submitting ${formatMoney(auction.nextBid)} on ${auction.title}...`)
      const result = await apiClient.placeBid(token, auction.id, auction.nextBid)
      const extension = result.bid?.extendedClose ? ' Anti-snipe extension triggered.' : ''
      await refreshAuctions(`Bid accepted at ${formatMoney(result.bid.amount)}.${extension}`)
    } catch (error) {
      setActionStatus(error.message || 'Bid failed.')
    }
  }

  const categories = useMemo(() => ['All', ...Array.from(new Set(auctions.map((auction) => auction.category).filter(Boolean)))], [auctions])
  const visibleAuctions = useMemo(() => filterAuctionsByFocus(auctions, focus, now), [auctions, focus, now])

  const metrics = useMemo(() => {
    const live = visibleAuctions.filter((auction) => auction.status === 'live').length
    const scheduled = visibleAuctions.filter((auction) => auction.status === 'scheduled').length
    const bids = visibleAuctions.reduce((sum, auction) => sum + (auction.bidCount || 0), 0)
    const endingSoon = visibleAuctions.filter((auction) => auctionEndsSoon(auction, now)).length

    return [
      { label: 'Live lots', value: live },
      { label: 'Scheduled', value: scheduled },
      { label: 'Room bids', value: bids },
      { label: 'Ending soon', value: endingSoon },
    ]
  }, [now, visibleAuctions])

  return (
    <main className="market-page market-page--auctions">
      <MarketplaceHero
        eyebrow="Auction floor"
        title="Live Lots, Real Pressure"
        description="Timed sports card and collectables auctions with casino-floor urgency, sports-bar visibility, reserve transparency, and verified bidder controls."
        primary={{ label: 'Join to Bid', href: '/register' }}
        secondary={{ label: 'Open The Vault', href: '/dashboard/vault' }}
        icon={<FaGavel />}
        metrics={metrics}
      />

      <section className="market-toolbar" aria-label="Auction filters">
        <label className="market-search">
          <FaMagnifyingGlass />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search player, set, league, or lot..." />
        </label>

        <label className="market-select">
          <FaFilter />
          <select value={status} onChange={(event) => setStatus(event.target.value)} aria-label="Auction status">
            {auctionStatuses.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </select>
        </label>

        <label className="market-select">
          <FaClock />
          <select value={type} onChange={(event) => setType(event.target.value)} aria-label="Auction type">
            {auctionTypes.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </select>
        </label>

        <label className="market-select">
          <FaFilter />
          <select value={category} onChange={(event) => setCategory(event.target.value)} aria-label="Auction category">
            {categories.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
        </label>

        <label className="market-select">
          <FaMedal />
          <select value={focus} onChange={(event) => setFocus(event.target.value)} aria-label="Auction focus">
            {auctionFocusOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </select>
        </label>
      </section>

      {actionStatus ? <div className="market-action-status">{actionStatus}</div> : null}
      {liveStatus ? <div className="market-live-status auction-floor-live-status">{liveStatus}</div> : null}

      <section className="auction-floor-strip" aria-label="Auction floor summary">
        <article><strong>{visibleAuctions.length}</strong><span>Showing</span></article>
        <article><strong>{auctions.filter((auction) => auction.featured).length}</strong><span>Featured</span></article>
        <article><strong>{auctions.filter((auction) => auctionEndsSoon(auction, now)).length}</strong><span>Ending soon</span></article>
        <article><strong>{auctions.filter((auction) => !auctionReserveMet(auction)).length}</strong><span>Reserve open</span></article>
      </section>

      <section className="auction-floor-grid" aria-label="Auction lots">
        {isLoading ? <MarketplaceCardSkeleton count={6} /> : visibleAuctions.map((auction) => (
          <AuctionCard
            key={auction.id}
            auction={auction}
            image={itemImage(auction)}
            now={now}
            onBid={handleBidAuction}
            onWatch={handleWatchAuction}
          />
        ))}
      </section>

      {!isLoading && !visibleAuctions.length ? <EmptyState message={loadState || 'No auction lots match this filter.'} /> : null}
    </main>
  )
}

function AuctionCard({ auction, image, now, onBid, onWatch }) {
  const clock = auctionClock(auction, now)
  const displayStatus = resolveAuctionStatus(auction, now)
  const reserveMet = auctionReserveMet(auction)
  const canBid = auctionCanBid(auction, now)

  return (
    <article className={`market-card auction-lot auction-lot--${displayStatus}`}>
      <a className="market-card__media" href={`/auctions/${auction.id}`}>
        {image ? <img src={image} alt={auction.title} /> : <span className="market-card__image-placeholder">No uploaded image</span>}
        <span className="market-card__status">{titleCase(displayStatus)}</span>
        {auction.featured ? <b className="market-card__featured"><FaMedal /> Featured</b> : null}
      </a>

      <div className="market-card__body">
        <div className="market-card__kicker">
          <span>{titleCase(auction.type)}</span>
          <span>{auction.category || 'Cards'}</span>
        </div>
        <h2><a href={`/auctions/${auction.id}`}>{auction.title}</a></h2>
        <p>{auction.subtitle || auction.description || 'Professional OBC auction listing'}</p>

        <div className="market-stats">
          <span><small>Current bid</small><strong>{formatMoney(auction.currentBid)}</strong></span>
          <span><small>Next bid</small><strong>{formatMoney(auction.nextBid)}</strong></span>
          <span><small>Reserve</small><strong>{reserveMet ? 'Met' : formatMoney(auction.reservePrice)}</strong></span>
        </div>

        <span className={`market-clock-label ${clock.parts.expired ? 'is-expired' : ''}`}>
          <FaClock /> {clock.label}
        </span>
        <CountdownTimer parts={clock.parts} />

        <div className="market-card__actions">
          <button className="market-button market-button--primary" type="button" disabled={!canBid} onClick={() => onBid(auction)}>
            <FaGavel /> {auctionActionLabel(auction)}
          </button>
          <button className="market-button market-button--ghost" type="button" onClick={() => onWatch(auction)}>
            <FaBell /> Watch Alerts
          </button>
        </div>
      </div>
    </article>
  )
}

function ShopLane() {
  const [items, setItems] = useState([])
  const [currentUser, setCurrentUser] = useState(() => getStoredUser())
  const [query, setQuery] = useState('')
  const [category, setCategory] = useState('All')
  const [availability, setAvailability] = useState('all')
  const [sort, setSort] = useState('newest')
  const [loadState, setLoadState] = useState('Loading buy-now shop...')
  const [actionStatus, setActionStatus] = useState('')
  const isLoading = loadState.startsWith('Loading')

  useEffect(() => {
    let mounted = true

    setLoadState('Loading buy-now shop...')
    apiClient.searchBuyNow({ q: query, category })
      .then((payload) => {
        if (!mounted) return
        setItems(payload.listings || [])
        setLoadState('')
      })
      .catch((error) => {
        if (!mounted) return
        setItems([])
        setLoadState(error.message || 'Buy-now shop is unavailable.')
      })

    return () => {
      mounted = false
    }
  }, [category, query])

  useEffect(() => {
    let mounted = true
    const token = getToken()

    if (!token) {
      setCurrentUser(null)
      return undefined
    }

    apiClient.me(token)
      .then((payload) => {
        if (!mounted) return
        setCurrentUser(payload.user)
        saveSession(token, payload.user)
      })
      .catch(() => {
        if (!mounted) return
        setCurrentUser(null)
      })

    return () => {
      mounted = false
    }
  }, [])

  const categories = useMemo(() => ['All', 'Cards', 'Memorabilia'], [])
  const visibleItems = useMemo(() => {
    const filtered = items.filter((item) => {
      if (availability === 'available') return item.inventory > 0
      if (availability === 'bundle') return item.inventory > 0 && item.bundleEligible
      if (availability === 'quick') return item.inventory > 0 && item.quickCheckout
      return true
    })

    return sortShopListings(filtered, sort)
  }, [availability, items, sort])

  const metrics = useMemo(() => {
    const memorabilia = items.filter((item) => item.category === 'Memorabilia').length
    const bundle = items.filter((item) => item.bundleEligible).length
    const quick = items.filter((item) => item.quickCheckout).length

    return [
      { label: 'Listings', value: items.length },
      { label: 'Memorabilia', value: memorabilia },
      { label: 'Bundle ready', value: bundle },
      { label: 'Quick pay', value: quick },
    ]
  }, [items])

  const refreshShop = async (message) => {
    const payload = await apiClient.searchBuyNow({ q: query, category })
    setItems(payload.listings || [])
    setLoadState('')
    if (message) setActionStatus(message)
  }

  const addToBundle = async (listing) => {
    const readiness = resolveShopActionReadiness(listing, currentUser, 'bundle')
    if (!readiness.ready) {
      if (readiness.actionPath) {
        navigateTo(readiness.actionPath)
      } else {
        setActionStatus(readiness.message)
      }
      return
    }

    const token = getToken()
    if (!token) {
      navigateTo('/login')
      return
    }

    try {
      setActionStatus(`Adding ${listing.title} to weekly bundle...`)
      const result = await apiClient.addBuyNowToBundle(token, listing.id)
      await refreshShop(`${listing.title} added. Friday total: ${formatMoney(result.bundle.totalDue)}.`)
    } catch (error) {
      setActionStatus(error.message || 'Could not add to weekly bundle.')
    }
  }

  const quickCheckout = async (listing) => {
    const readiness = resolveShopActionReadiness(listing, currentUser, 'checkout')
    if (!readiness.ready) {
      if (readiness.actionPath) {
        navigateTo(readiness.actionPath)
      } else {
        setActionStatus(readiness.message)
      }
      return
    }

    const token = getToken()
    if (!token) {
      navigateTo('/login')
      return
    }

    try {
      setActionStatus(`Checking out ${listing.title}...`)
      const result = await apiClient.quickCheckoutBuyNow(token, listing.id)
      await refreshShop(`Paid ${formatMoney(result.payment.amount)}. Order is ready for fulfillment.`)
    } catch (error) {
      if (hasStripePaymentRecovery(error)) {
        try {
          setActionStatus('Stripe needs card authentication. Opening secure confirmation...')
          await confirmStripePaymentRecovery(error)
          await refreshShop('Stripe authentication complete. Payment confirmation is being reconciled.')
          return
        } catch (recoveryError) {
          setActionStatus(recoveryError.message || 'Stripe authentication failed.')
          return
        }
      }

      setActionStatus(error.message || 'Quick checkout failed.')
    }
  }

  return (
    <main className="market-page market-page--shop">
      <MarketplaceHero
        eyebrow="Buy-now shop"
        title="Cleaner Lane, Fast Checkout"
        description="Fixed-price cards and memorabilia for collectors who want the hit now. Search, filter, bundle, or quick-pay without losing the OBC energy."
        primary={{ label: 'Create Account', href: '/register' }}
        secondary={{ label: 'Manage Bundle', href: '/dashboard/bundle' }}
        icon={<FaStore />}
        metrics={metrics}
      />

      <section className="market-toolbar" aria-label="Shop filters">
        <label className="market-search">
          <FaMagnifyingGlass />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search cards, boxes, autos, memorabilia..." />
        </label>

        <label className="market-select">
          <FaFilter />
          <select value={category} onChange={(event) => setCategory(event.target.value)} aria-label="Shop category">
            {categories.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
        </label>

        <label className="market-select">
          <FaSliders />
          <select value={availability} onChange={(event) => setAvailability(event.target.value)} aria-label="Shop availability">
            {shopAvailabilityOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </select>
        </label>

        <label className="market-select">
          <FaSliders />
          <select value={sort} onChange={(event) => setSort(event.target.value)} aria-label="Shop sort">
            {shopSortOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </select>
        </label>
      </section>

      {actionStatus ? <div className="market-action-status">{actionStatus}</div> : null}

      <section className="shop-lane-strip" aria-label="Buy-now lane summary">
        <article><strong>{visibleItems.length}</strong><span>Showing</span></article>
        <article><strong>{items.filter((item) => item.inventory <= 2).length}</strong><span>Low stock</span></article>
        <article><strong>{items.filter((item) => item.category === 'Memorabilia').length}</strong><span>Memorabilia</span></article>
        <article><strong>Friday</strong><span>Bundle checkout</span></article>
      </section>

      <section className="shop-grid" aria-label="Buy-now listings">
        {isLoading ? <MarketplaceCardSkeleton count={8} /> : visibleItems.map((listing) => (
          <ShopCard
            key={listing.id}
            image={itemImage(listing)}
            listing={listing}
            user={currentUser}
            onAddToBundle={addToBundle}
            onQuickCheckout={quickCheckout}
          />
        ))}
      </section>

      {!isLoading && !visibleItems.length ? <EmptyState message={loadState || 'No buy-now listings match this filter.'} /> : null}
    </main>
  )
}

function ShopCard({ image, listing, onAddToBundle, onQuickCheckout, user }) {
  const checkoutReadiness = resolveShopActionReadiness(listing, user, 'checkout')
  const bundleReadiness = resolveShopActionReadiness(listing, user, 'bundle')
  const checkoutLabel = checkoutReadiness.ready ? 'Quick Pay' : checkoutReadiness.actionLabel || 'Unavailable'
  const bundleLabel = bundleReadiness.ready ? 'Add Bundle' : bundleReadiness.actionLabel || 'Unavailable'

  return (
    <article className="market-card shop-item">
      <a className="market-card__media" href={`/shop/${listing.id}`}>
        {image ? <img src={image} alt={listing.title} /> : <span className="market-card__image-placeholder">No uploaded image</span>}
        <span className="market-card__status">{listing.category}</span>
        {listing.featured ? <b className="market-card__featured"><FaMedal /> Featured</b> : null}
      </a>

      <div className="market-card__body">
        <div className="market-card__kicker">
          <span>{listing.bundleEligible ? 'Bundle eligible' : 'Ship direct'}</span>
          <span>{listing.quickCheckout ? 'Quick checkout' : 'Standard checkout'}</span>
        </div>
        <h2><a href={`/shop/${listing.id}`}>{listing.title}</a></h2>
        <p>{listing.category === 'Memorabilia' ? 'Sports bar wall-piece lane' : 'Collector-ready fixed price listing'}</p>

        <div className="market-stats market-stats--shop">
          <span><small>Price</small><strong>{formatMoney(listing.price)}</strong></span>
          <span><small>Inventory</small><strong>{listingInventoryLabel(listing)}</strong></span>
        </div>

        <div className="shop-card-flags">
          <span>{listing.category}</span>
          <span>{listing.bundleEligible ? 'Weekly hold' : 'Direct ship'}</span>
          <span>{listing.quickCheckout ? 'Quick pay' : 'Standard pay'}</span>
        </div>

        <div className="shop-card-readiness" aria-label="Listing action readiness">
          <span className={`shop-card-readiness__item shop-card-readiness__item--${checkoutReadiness.code}`}>
            <FaCreditCard /> {checkoutReadiness.title}
          </span>
          <span className={`shop-card-readiness__item shop-card-readiness__item--${bundleReadiness.code}`}>
            <FaBox /> {bundleReadiness.title}
          </span>
        </div>

        <div className="market-card__actions">
          <button className="market-button market-button--primary" type="button" disabled={!shopActionIsAvailable(checkoutReadiness)} onClick={() => onQuickCheckout(listing)}>
            <FaCreditCard /> {checkoutLabel}
          </button>
          <button className="market-button market-button--ghost" type="button" disabled={!shopActionIsAvailable(bundleReadiness)} onClick={() => onAddToBundle(listing)}>
            <FaBox /> {bundleLabel}
          </button>
        </div>
      </div>
    </article>
  )
}

function AuctionDetail({ auctionId }) {
  const [detail, setDetail] = useState(null)
  const [currentUser, setCurrentUser] = useState(() => getStoredUser())
  const [loadState, setLoadState] = useState('Loading auction lot...')
  const [actionStatus, setActionStatus] = useState('')
  const [liveStatus, setLiveStatus] = useState('Connecting live bid room...')
  const [bidAmount, setBidAmount] = useState('')
  const [now, setNow] = useState(Date.now())

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    let mounted = true

    setLoadState('Loading auction lot...')
    apiClient.auctionDetail(auctionId)
      .then((payload) => {
        if (!mounted) return
        setDetail(payload)
        setBidAmount(String(payload.auction?.nextBid || ''))
        setLoadState('')
      })
      .catch((error) => {
        if (!mounted) return
        setDetail(null)
        setLoadState(error.message || 'Auction lot was not found.')
      })

    return () => {
      mounted = false
    }
  }, [auctionId])

  useEffect(() => {
    if (typeof EventSource === 'undefined') {
      setLiveStatus('Live bid room unavailable in this browser.')
      return undefined
    }

    const events = new EventSource(apiClient.auctionEventsUrl(auctionId))

    events.addEventListener('connected', () => {
      setLiveStatus('Live bid room connected.')
    })

    events.addEventListener('auction-snapshot', (event) => {
      const payload = JSON.parse(event.data)

      setDetail({
        auction: payload.auction,
        bids: payload.bids || [],
      })
      setBidAmount(String(payload.auction?.nextBid || ''))
      setLiveStatus('Live bid room synced.')
    })

    events.addEventListener('bid-accepted', (event) => {
      const payload = JSON.parse(event.data)

      setDetail((current) => {
        if (!current?.auction || payload.auction.id !== current.auction.id) return current

        const existingBids = current.bids || []
        const nextBids = existingBids.some((bid) => bid.id === payload.bid.id)
          ? existingBids
          : [payload.bid, ...existingBids].slice(0, 25)

        return {
          ...current,
          auction: payload.auction,
          bids: nextBids,
        }
      })
      setBidAmount(String(payload.auction?.nextBid || ''))
      setLiveStatus(payload.bid?.extendedClose ? 'Live bid accepted. Anti-snipe timer extended.' : 'Live bid accepted.')
    })

    events.addEventListener('auction-status-changed', (event) => {
      const payload = JSON.parse(event.data)

      setDetail((current) => {
        if (!current?.auction || payload.auction.id !== current.auction.id) return current

        return {
          ...current,
          auction: payload.auction,
        }
      })
      setBidAmount(String(payload.auction?.nextBid || ''))
      setLiveStatus(`${payload.auction?.title || 'Auction'} is now ${titleCase(payload.auction?.status || 'updated')}.`)
    })

    events.onerror = () => {
      setLiveStatus('Reconnecting live bid room...')
    }

    return () => {
      events.close()
    }
  }, [auctionId])

  useEffect(() => {
    let mounted = true
    const token = getToken()

    if (!token) {
      setCurrentUser(null)
      return undefined
    }

    apiClient.me(token)
      .then((payload) => {
        if (!mounted) return
        setCurrentUser(payload.user)
        saveSession(token, payload.user)
      })
      .catch(() => {
        if (!mounted) return
        setCurrentUser(null)
      })

    return () => {
      mounted = false
    }
  }, [auctionId])

  const refreshDetail = async (message) => {
    const payload = await apiClient.auctionDetail(auctionId)
    setDetail(payload)
    setBidAmount(String(payload.auction?.nextBid || ''))
    setLoadState('')
    if (message) setActionStatus(message)
  }

  const requireSession = () => {
    const token = getToken()
    if (!token) {
      navigateTo('/login')
      return null
    }

    return token
  }

  const handleWatch = async () => {
    const token = requireSession()
    if (!token || !detail?.auction) return

    try {
      setActionStatus(`Adding ${detail.auction.title} to your watchlist...`)
      await apiClient.addToWatchlist(token, detail.auction.id)
      setActionStatus(`${detail.auction.title} is now in your watchlist.`)
    } catch (error) {
      setActionStatus(error.message || 'Could not update watchlist.')
    }
  }

  const handleBid = async (event) => {
    event.preventDefault()
    if (!detail?.auction) return

    const readiness = resolveBidReadiness(detail.auction, currentUser)
    if (!readiness.ready) {
      if (readiness.actionPath) {
        navigateTo(readiness.actionPath)
      } else {
        setActionStatus(readiness.message)
      }
      return
    }

    const token = requireSession()
    if (!token) return

    const amount = Number(bidAmount)

    if (!auctionCanBid(detail.auction)) {
      setActionStatus('This lot is not open for bidding.')
      return
    }

    if (!Number.isFinite(amount) || amount < detail.auction.nextBid) {
      setActionStatus(`Minimum next bid is ${formatMoney(detail.auction.nextBid)}.`)
      return
    }

    try {
      setActionStatus(`Submitting ${formatMoney(amount)} bid...`)
      const result = await apiClient.placeBid(token, detail.auction.id, amount)
      const extension = result.bid?.extendedClose ? ' Anti-snipe extension triggered.' : ''
      await refreshDetail(`Bid accepted at ${formatMoney(result.bid.amount)}.${extension}`)
    } catch (error) {
      setActionStatus(error.message || 'Bid failed.')
    }
  }

  if (!detail && loadState.startsWith('Loading')) {
    return <main className="market-page market-detail"><MarketplaceDetailSkeleton /></main>
  }

  if (!detail) {
    return <main className="market-page"><EmptyState message={loadState} /></main>
  }

  const auction = detail.auction
  const clock = auctionClock(auction, now)
  const displayStatus = resolveAuctionStatus(auction, now)
  const reserveMet = auctionReserveMet(auction)
  const canBid = auctionCanBid(auction, now)
  const bidReadiness = resolveBidReadiness(auction, currentUser)
  const image = itemImage(auction)
  const startsAt = auction.startsAt
    ? new Date(auction.startsAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : 'Not scheduled'
  const endsAt = auction.endsAt
    ? new Date(auction.endsAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : 'Not scheduled'

  return (
    <main className="market-page market-detail market-detail--auction auction-room">
      <div className="auction-room__shell">
        <a className="market-back-link auction-room__back" href="/live-auctions"><FaArrowLeft /> Auction floor</a>

        <section className="auction-room__stage">
          <div className="auction-room__media">
            {image ? <img src={image} alt={auction.title} /> : <span className="market-card__image-placeholder">No uploaded image</span>}
            <div className="auction-room__chips" aria-label="Auction status">
              <span className={`auction-room__chip is-${displayStatus}`}>{titleCase(displayStatus)}</span>
              {auction.featured ? <span className="auction-room__chip auction-room__chip--feature"><FaMedal /> Featured</span> : null}
            </div>
            <div className="auction-room__image-board">
              <span><small>Format</small><strong>{titleCase(auction.type)}</strong></span>
              <span><small>Reserve</small><strong>{reserveMet ? 'Met' : 'Open'}</strong></span>
            </div>
          </div>

          <div className="auction-room__content">
            <div className="auction-room__header">
              <span className="market-detail__eyebrow"><FaShieldHalved /> Verified bidder auction</span>
              <span className="auction-room__live"><FaClock /> {liveStatus}</span>
            </div>

            <div className="auction-room__title-block">
              <p className="auction-room__subtitle">{auction.subtitle || `${auction.category || 'Collector'} lot`}</p>
              <h1>{auction.title}</h1>
              <p>{auction.description || 'Professional OBC auction listing with live-style bidding controls, transparent bid rules, and verified bidder protection.'}</p>
            </div>

            <div className="auction-room__scoreboard" aria-label="Current auction pricing">
              <span className="auction-room__score auction-room__score--primary"><small>Current bid</small><strong>{formatMoney(auction.currentBid)}</strong></span>
              <span className="auction-room__score"><small>Next bid</small><strong>{formatMoney(auction.nextBid)}</strong></span>
              <span className="auction-room__score"><small>Reserve</small><strong>{reserveMet ? 'Met' : formatMoney(auction.reservePrice)}</strong></span>
              <span className="auction-room__score"><small>Bids</small><strong>{auction.bidCount || 0}</strong></span>
            </div>

            <div className="auction-room__countdown-card">
              <span className={`market-clock-label market-clock-label--detail ${clock.parts.expired ? 'is-expired' : ''}`}>
                <FaClock /> {clock.label}
              </span>
              <CountdownTimer className="market-countdown--detail" parts={clock.parts} />
            </div>

            <div className="auction-bid-console auction-room__bid-console">
              <div className="auction-bid-console__head">
                <div>
                  <FaGavel />
                  <strong>{canBid ? 'Bid console' : titleCase(displayStatus)}</strong>
                </div>
                <span>{canBid ? `Minimum next bid is ${formatMoney(auction.nextBid)}.` : 'This lot is not currently accepting bids.'}</span>
              </div>
              <div className={`auction-bid-readiness auction-bid-readiness--${bidReadiness.code}`}>
                {bidReadiness.ready ? <FaCircleCheck /> : <FaTriangleExclamation />}
                <div>
                  <strong>{bidReadiness.title}</strong>
                  <span>{bidReadiness.message}</span>
                </div>
                {bidReadiness.actionPath ? (
                  <button type="button" onClick={() => navigateTo(bidReadiness.actionPath)}>
                    {bidReadiness.actionLabel}
                  </button>
                ) : null}
              </div>
              <form className="auction-bid-console__form" onSubmit={handleBid}>
                <label>
                  Bid amount
                  <input
                    type="number"
                    min={auction.nextBid || 0}
                    step={auction.minimumIncrement || 1}
                    value={bidAmount}
                    disabled={!bidReadiness.ready}
                    onChange={(event) => setBidAmount(event.target.value)}
                  />
                </label>
                <button className="market-button market-button--primary" type="submit" disabled={!bidReadiness.ready}>
                  <FaGavel /> Submit Bid
                </button>
              </form>
            </div>

            {actionStatus ? <div className="market-action-status auction-room__status-message">{actionStatus}</div> : null}

            <div className="market-detail__actions auction-room__actions">
              <button className="market-button market-button--ghost" type="button" onClick={handleWatch}><FaHeart /> Watch Lot</button>
              <a className="market-button market-button--ghost" href="/dashboard/vault"><FaListUl /> Open Vault</a>
            </div>
          </div>
        </section>

        <section className="auction-room__lower">
          <article className="auction-detail-panel auction-detail-panel--lot">
            <div className="market-panel-head">
              <h2>Lot Details</h2>
              <span>{auction.category || 'Cards'}</span>
            </div>
            <div className="auction-room__spec-list">
              <span><small>Starts</small><strong>{startsAt}</strong></span>
              <span><small>Ends</small><strong>{endsAt}</strong></span>
              <span><small>Category</small><strong>{auction.category || 'Cards'}</strong></span>
              <span><small>Homepage</small><strong>{auction.featured ? 'Featured' : 'Standard'}</strong></span>
            </div>
          </article>

          <article className="auction-detail-panel auction-detail-panel--rules">
            <h2>Bid Rules</h2>
            <div className="auction-rule-grid">
              <span><strong>{titleCase(auction.type)}</strong><small>Auction format</small></span>
              <span><strong>{formatMoney(auction.minimumIncrement)}</strong><small>Minimum increment</small></span>
              <span><strong>{reserveMet ? 'Met' : 'Not met'}</strong><small>Reserve status</small></span>
              <span><strong>{canBid ? 'Open' : 'Closed'}</strong><small>Bidding status</small></span>
            </div>
            <p><FaTriangleExclamation /> OBC requires a verified card before bidding. Anti-snipe lots may extend if a bid lands near the close.</p>
          </article>

          <article className="auction-detail-panel auction-detail-panel--history">
            <div className="market-panel-head">
              <h2>Bid History</h2>
              <button type="button" onClick={() => refreshDetail('Bid history refreshed.')}><FaRotateRight /> Refresh</button>
            </div>
            <div className="market-history auction-room__history">
              {(detail.bids || []).map((bid) => (
                <div key={bid.id}>
                  <span>{bid.bidderLabel}</span>
                  <strong>{formatMoney(bid.amount)}</strong>
                  <small>{bid.extendedClose ? 'Timer extended' : new Date(bid.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</small>
                </div>
              ))}
              {!detail.bids?.length ? <p>No bids recorded yet.</p> : null}
            </div>
          </article>
        </section>
      </div>
    </main>
  )
}

function ShopDetail({ listingId }) {
  const [detail, setDetail] = useState(null)
  const [currentUser, setCurrentUser] = useState(() => getStoredUser())
  const [loadState, setLoadState] = useState('Loading product...')
  const [actionStatus, setActionStatus] = useState('')

  useEffect(() => {
    let mounted = true

    setLoadState('Loading product...')
    apiClient.buyNowDetail(listingId)
      .then((payload) => {
        if (!mounted) return
        setDetail(payload.listing)
        setLoadState('')
      })
      .catch((error) => {
        if (!mounted) return
        setDetail(null)
        setLoadState(error.message || 'Product was not found.')
      })

    return () => {
      mounted = false
    }
  }, [listingId])

  useEffect(() => {
    let mounted = true
    const token = getToken()

    if (!token) {
      setCurrentUser(null)
      return undefined
    }

    apiClient.me(token)
      .then((payload) => {
        if (!mounted) return
        setCurrentUser(payload.user)
        saveSession(token, payload.user)
      })
      .catch(() => {
        if (!mounted) return
        setCurrentUser(null)
      })

    return () => {
      mounted = false
    }
  }, [listingId])

  const refreshListing = async (message) => {
    const payload = await apiClient.buyNowDetail(listingId)
    setDetail(payload.listing)
    setLoadState('')
    if (message) setActionStatus(message)
  }

  const handleAction = async (kind) => {
    if (!detail) return

    const readiness = resolveShopActionReadiness(detail, currentUser, kind)
    if (!readiness.ready) {
      if (readiness.actionPath) {
        navigateTo(readiness.actionPath)
      } else {
        setActionStatus(readiness.message)
      }
      return
    }

    const token = getToken()
    if (!token) {
      navigateTo('/login')
      return
    }

    try {
      setActionStatus(kind === 'bundle' ? 'Adding item to weekly bundle...' : 'Running quick checkout...')
      const result = kind === 'bundle'
        ? await apiClient.addBuyNowToBundle(token, detail.id)
        : await apiClient.quickCheckoutBuyNow(token, detail.id)

      await refreshListing(kind === 'bundle'
        ? `Added to bundle. Friday total is ${formatMoney(result.bundle.totalDue)}.`
        : `Paid ${formatMoney(result.payment.amount)}. Order is ready for fulfillment.`)
    } catch (error) {
      if (kind !== 'bundle' && hasStripePaymentRecovery(error)) {
        try {
          setActionStatus('Stripe needs card authentication. Opening secure confirmation...')
          await confirmStripePaymentRecovery(error)
          await refreshListing('Stripe authentication complete. Payment confirmation is being reconciled.')
          return
        } catch (recoveryError) {
          setActionStatus(recoveryError.message || 'Stripe authentication failed.')
          return
        }
      }

      setActionStatus(error.message || 'Action failed.')
    }
  }

  if (!detail && loadState.startsWith('Loading')) {
    return <main className="market-page market-detail"><MarketplaceDetailSkeleton /></main>
  }

  if (!detail) {
    return <main className="market-page"><EmptyState message={loadState} /></main>
  }

  const image = itemImage(detail)
  const checkoutReadiness = resolveShopActionReadiness(detail, currentUser, 'checkout')
  const bundleReadiness = resolveShopActionReadiness(detail, currentUser, 'bundle')
  const checkoutLabel = checkoutReadiness.ready ? 'Quick Checkout' : checkoutReadiness.actionLabel || 'Unavailable'
  const bundleLabel = bundleReadiness.ready ? 'Add To Weekly Bundle' : bundleReadiness.actionLabel || 'Unavailable'

  return (
    <main className="market-page market-detail">
      <a className="market-back-link" href="/shop">Back to buy-now shop</a>
      <section className="market-detail__hero">
        <div className="market-detail__image">
          {image ? <img src={image} alt={detail.title} /> : <span className="market-card__image-placeholder">No uploaded image</span>}
          <span>{detail.category}</span>
        </div>
        <div className="market-detail__content">
          <span className="market-detail__eyebrow"><FaCartShopping /> Fixed-price listing</span>
          <h1>{detail.title}</h1>
          <p>Clean buy-now lane for collectors who want a direct purchase path with bundle-friendly fulfillment. Choose immediate checkout or hold eligible items for the Friday shipping stack.</p>

          <div className="market-stats market-stats--detail">
            <span><small>Price</small><strong>{formatMoney(detail.price)}</strong></span>
            <span><small>Inventory</small><strong>{listingInventoryLabel(detail)}</strong></span>
            <span><small>Bundle</small><strong>{detail.bundleEligible ? 'Eligible' : 'Direct ship'}</strong></span>
            <span><small>Checkout</small><strong>{detail.quickCheckout ? 'Quick pay' : 'Standard'}</strong></span>
          </div>

          {actionStatus ? <div className="market-action-status">{actionStatus}</div> : null}

          <div className="shop-decision-panel">
            <article className={`shop-action-readiness shop-action-readiness--${checkoutReadiness.code}`}>
              <FaCreditCard />
              <strong>Quick checkout</strong>
              <span>{checkoutReadiness.message}</span>
              {checkoutReadiness.actionPath ? (
                <button type="button" onClick={() => navigateTo(checkoutReadiness.actionPath)}>{checkoutReadiness.actionLabel}</button>
              ) : null}
            </article>
            <article className={`shop-action-readiness shop-action-readiness--${bundleReadiness.code}`}>
              <FaBox />
              <strong>Weekly bundle</strong>
              <span>{bundleReadiness.message}</span>
              {bundleReadiness.actionPath ? (
                <button type="button" onClick={() => navigateTo(bundleReadiness.actionPath)}>{bundleReadiness.actionLabel}</button>
              ) : null}
            </article>
          </div>

          <div className="market-detail__actions">
            <button className="market-button market-button--primary" type="button" disabled={!shopActionIsAvailable(checkoutReadiness)} onClick={() => handleAction('checkout')}>
              <FaCreditCard /> {checkoutLabel}
            </button>
            <button className="market-button market-button--ghost" type="button" disabled={!shopActionIsAvailable(bundleReadiness)} onClick={() => handleAction('bundle')}>
              <FaBox /> {bundleLabel}
            </button>
          </div>
        </div>
      </section>

      <section className="market-detail__panels">
        <article>
          <h2>Fulfillment</h2>
          <div className="shop-detail-list">
            <span><strong>Quick pay</strong><small>{detail.quickCheckout ? 'Available' : 'Not enabled'}</small></span>
            <span><strong>Weekly hold</strong><small>{detail.bundleEligible ? 'Available' : 'Not eligible'}</small></span>
            <span><strong>Inventory</strong><small>{listingInventoryLabel(detail)}</small></span>
          </div>
        </article>
        <article>
          <h2>Collector Confidence</h2>
          <p>OBC admin controls listing status, inventory, imagery, and homepage feature windows from the operations dashboard. Sold-out items are removed from public checkout paths automatically.</p>
        </article>
      </section>
    </main>
  )
}

function MarketplaceHero({ description, eyebrow, icon, metrics, primary, secondary, title }) {
  return (
    <section className="market-hero">
      <div className="market-hero__copy">
        <span className="market-hero__eyebrow">{icon}{eyebrow}</span>
        <h1>{title}</h1>
        <p>{description}</p>
        <div className="market-hero__actions">
          <a className="market-button market-button--primary" href={primary.href}>{primary.label}</a>
          <a className="market-button market-button--ghost" href={secondary.href}>{secondary.label}</a>
        </div>
      </div>
      <div className="market-hero__board">
        <span>OBC scoreboard</span>
        <div>
          {metrics.map((metric) => (
            <article key={metric.label}>
              <strong>{metric.value}</strong>
              <small>{metric.label}</small>
            </article>
          ))}
        </div>
        <p><FaTruckFast /> Weekly holds and verified bidding keep the room moving.</p>
      </div>
    </section>
  )
}

function EmptyState({ message }) {
  return (
    <section className="market-empty">
      <FaShieldHalved />
      <h2>{message}</h2>
      <p>Check the filters or try again once the OBC API is available.</p>
    </section>
  )
}

function SkeletonLine({ className = '' }) {
  return <span className={`market-skeleton-line ${className}`} aria-hidden="true" />
}

function MarketplaceCardSkeleton({ count = 6 }) {
  return Array.from({ length: count }, (_, index) => (
    <article className="market-card market-card--skeleton" aria-hidden="true" key={`market-skeleton-${index}`}>
      <div className="market-card__media market-skeleton-block" />
      <div className="market-card__body">
        <div className="market-card__kicker">
          <SkeletonLine className="is-short" />
          <SkeletonLine className="is-short" />
        </div>
        <SkeletonLine className="is-title" />
        <SkeletonLine />
        <SkeletonLine className="is-wide" />
        <div className="market-stats">
          <span><SkeletonLine /></span>
          <span><SkeletonLine /></span>
          <span><SkeletonLine /></span>
        </div>
        <div className="market-countdown">
          <span><SkeletonLine /></span>
          <span><SkeletonLine /></span>
          <span><SkeletonLine /></span>
          <span><SkeletonLine /></span>
        </div>
      </div>
    </article>
  ))
}

function MarketplaceDetailSkeleton() {
  return (
    <>
      <SkeletonLine className="market-detail-skeleton__back" />
      <section className="market-detail__hero market-detail__hero--skeleton" aria-hidden="true">
        <div className="market-detail__image market-skeleton-block" />
        <div className="market-detail__content">
          <SkeletonLine className="is-short" />
          <SkeletonLine className="is-hero" />
          <SkeletonLine />
          <SkeletonLine className="is-wide" />
          <div className="market-countdown market-countdown--detail">
            <span><SkeletonLine /></span>
            <span><SkeletonLine /></span>
            <span><SkeletonLine /></span>
            <span><SkeletonLine /></span>
          </div>
          <div className="market-stats market-stats--detail">
            <span><SkeletonLine /></span>
            <span><SkeletonLine /></span>
            <span><SkeletonLine /></span>
            <span><SkeletonLine /></span>
          </div>
        </div>
      </section>
    </>
  )
}
